import { leadsApi } from '@/lib/api/leads';
import { registerQuoteFlow, restoreQuoteFlow } from '@/lib/tracking';

// The visitor's quote arm, decided by the server when they open the page (not when the wizard opens), so the
// form can be worded for it with no wait. Sticky on this device until the signed token expires; submit
// sends the token back and the server honors it. `?qf=instant` (or `email`) on any public page forces the arm, to try a
// flow end to end in production; the server records those leads as `forced`.

export type QuoteFlowArm = 'instant' | 'email';
export interface QuoteFlowAssignment {
  flow: QuoteFlowArm;
  token: string;
  expiresAt: number;
  /** Instant-arm probability in effect when this visitor was rolled */
  split?: number;
  /** Picked with `?qf=`, not rolled */
  forced?: boolean;
}

const STORAGE_KEY = 'casa-aragon:quote-flow';
let inFlight: Promise<QuoteFlowAssignment | null> | null = null;

/** The stored assignment while its token is still valid; null when none yet (the form then uses today's wording). */
export function getQuoteFlowAssignment(): QuoteFlowAssignment | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as QuoteFlowAssignment;
    const valid =
      (parsed.flow === 'instant' || parsed.flow === 'email') &&
      typeof parsed.token === 'string' &&
      typeof parsed.expiresAt === 'number' &&
      parsed.expiresAt > Date.now();
    return valid ? parsed : null;
  } catch {
    return null;
  }
}

function forcedArm(search: string): QuoteFlowArm | undefined {
  const qf = new URLSearchParams(search).get('qf');
  return qf === 'instant' || qf === 'email' ? qf : undefined;
}

/** Fetches and stores the assignment once per visitor; never throws (a failure just leaves today's wording). */
export function ensureQuoteFlowAssignment(
  search: string = window.location.search,
): Promise<QuoteFlowAssignment | null> {
  const force = forcedArm(search);
  const existing = getQuoteFlowAssignment();
  if (existing && (!force || (existing.forced && existing.flow === force))) {
    // A returning visitor keeps their arm on every event of this visit, not only in the wizard
    restoreQuoteFlow(existing.flow, existing.split);
    return Promise.resolve(existing);
  }
  if (inFlight) return inFlight;
  inFlight = Promise.resolve()
    .then(() => leadsApi.quoteFlow(force))
    .then((res) => {
      const assignment: QuoteFlowAssignment = force ? { ...res.data, forced: true } : res.data;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(assignment));
      } catch {
        // Storage unavailable: this page view still has the answer in memory only through the next read
      }
      registerQuoteFlow(assignment.flow, force && 'forced', { stage: 'visit', split: assignment.split });
      return assignment;
    })
    .catch(() => null)
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}
